import { Calendar, Clock, MapPin, FileText, Users, DollarSign } from "lucide-react";
import { ZipAutoFillInput } from "../../components/ZipAutoFillInput";

export default function JobForm({ jobData, setJobData, surveyors }) {

    const handleSurveyorChange = (e) => {
        const surveyorId = parseInt(e.target.value);
        if (!surveyorId) return;
        setJobData(prev => ({
            ...prev,
            surveyors: [{ id: surveyorId, amount: 0 }]
        }));
    };

    const handleUpdateSurveyorAmount = (amount) => {
        if (jobData.surveyors.length === 0) return;

        setJobData(prev => ({
            ...prev,
            surveyors: [{ ...prev.surveyors[0], amount: parseFloat(amount) || 0 }]
        }));
    };

    const selectedSurveyorId = jobData.surveyors.length > 0 ? jobData.surveyors[0].id : "";
    const selectedSurveyorAmount = jobData.surveyors.length > 0 ? jobData.surveyors[0].amount : "";

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-lg font-bold text-gray-900 mb-1">Job Details & Assignment</h2>
                <p className="text-xs text-gray-600 mb-5">Schedule the job and assign surveyors</p>

                <div className="space-y-6">
                    <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-1.5">
                            <Calendar className="w-4 h-4" />
                            Schedule
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                    <Calendar className="w-3.5 h-3.5" />
                                    Start Date <span className="text-gray-400 font-normal">(Optional)</span>
                                </label>
                                <input
                                    type="date"
                                    value={jobData.start_date}
                                    onChange={(e) => setJobData(prev => ({ ...prev, start_date: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                    <Clock className="w-3.5 h-3.5" />
                                    Start Time <span className="text-gray-400 font-normal">(Optional)</span>
                                </label>
                                <input
                                    type="time"
                                    value={jobData.start_time}
                                    onChange={(e) => setJobData(prev => ({ ...prev, start_time: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5 flex items-center gap-1.5">
                                    <Clock className="w-3.5 h-3.5" />
                                    End Time <span className="text-gray-400 font-normal">(Optional)</span>
                                </label>
                                <input
                                    type="time"
                                    value={jobData.end_time}
                                    onChange={(e) => setJobData(prev => ({ ...prev, end_time: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                        </div>
                    </div>

                    <hr className="border-gray-200" />

                    <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-1.5">
                            <MapPin className="w-4 h-4" />
                            Location
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    Zip Code
                                </label>
                                <ZipAutoFillInput
                                    zip={jobData.zip_code || ""}
                                    onZipChange={(zip) => setJobData(prev => ({ ...prev, zip_code: zip || "" }))}
                                    onAddressResolved={({ city, state, country }) => {
                                        const parts = [city, state, country].filter(Boolean);
                                        if (parts.length > 0) {
                                            setJobData(prev => ({ ...prev, location: parts.join(', ') }));
                                        }
                                    }}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    Location
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g., Miami Marina"
                                    value={jobData.location}
                                    onChange={(e) => setJobData(prev => ({ ...prev, location: e.target.value }))}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                />
                            </div>
                        </div>
                    </div>

                    <hr className="border-gray-200" />

                    <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-1.5">
                            <FileText className="w-4 h-4" />
                            Instructions & Notes
                        </h3>
                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    Visit Instructions
                                </label>
                                <textarea
                                    placeholder="Add instructions for the surveyor (e.g., Call upon arrival, Gate code, etc.)"
                                    value={jobData.visit_instructions}
                                    onChange={(e) => setJobData(prev => ({ ...prev, visit_instructions: e.target.value }))}
                                    rows={3}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs resize-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                    Internal Notes
                                </label>
                                <textarea
                                    placeholder="Add any internal notes about this job..."
                                    value={jobData.notes}
                                    onChange={(e) => setJobData(prev => ({ ...prev, notes: e.target.value }))}
                                    rows={3}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs resize-none"
                                />
                            </div>
                        </div>
                    </div>

                    <hr className="border-gray-200" />

                    <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-1.5">
                            <Users className="w-4 h-4" />
                            Assign Surveyor
                        </h3>

                        {surveyors.length > 0 && (
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                            Select Surveyor
                                        </label>
                                        <div className="relative">
                                            <select
                                                value={selectedSurveyorId}
                                                onChange={handleSurveyorChange}
                                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs appearance-none bg-white"
                                            >
                                                <option value="">Select Surveyor</option>
                                                {surveyors.map(surveyor => (
                                                    <option key={surveyor.id} value={surveyor.id}>
                                                        {surveyor.full_name || surveyor.name || surveyor.user?.name || `Surveyor ${surveyor.id}`}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </div>
                                    </div>

                                    {selectedSurveyorId && (
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                                Payment Amount ($)
                                            </label>
                                            <div className="relative">
                                                <DollarSign className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
                                                <input
                                                    type="number"
                                                    placeholder="0.00"
                                                    value={selectedSurveyorAmount}
                                                    onChange={(e) => handleUpdateSurveyorAmount(e.target.value)}
                                                    className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {selectedSurveyorId && (
                                    <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                                        <h4 className="text-xs font-bold text-gray-900 mb-3 border-b border-gray-100 pb-2">Surveyor Details</h4>
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                                                {(() => {
                                                    const s = surveyors.find(sur => sur.id === selectedSurveyorId);
                                                    const name = s?.full_name || s?.name || s?.user?.name || "?";
                                                    return name.charAt(0).toUpperCase();
                                                })()}
                                            </div>
                                            <div className="space-y-1">
                                                {(() => {
                                                    const s = surveyors.find(sur => sur.id === selectedSurveyorId);
                                                    return (
                                                        <>
                                                            <p className="text-sm font-bold text-gray-900">
                                                                {s?.full_name || s?.name || s?.user?.name || "Unknown Name"}
                                                            </p>
                                                            <div className="flex flex-col gap-1">
                                                                <p className="text-xs text-gray-500 flex items-center gap-1.5">
                                                                    <Users className="w-3 h-3" />
                                                                    {s?.email || s?.user?.email || "No email"}
                                                                </p>
                                                                <p className="text-xs text-gray-500 flex items-center gap-1.5">
                                                                    <Users className="w-3 h-3" />
                                                                    {s?.phone || s?.user?.phone || "No phone"}
                                                                </p>
                                                            </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
